import { fetchCandles } from '@/modules/graph-view/services/candlesService'
import { fetchPivotMarkers } from '@/modules/graph-view/services/pivotsService'
import { supabase } from '@/lib/supabase'

export type Swing = { hi?: { idx: number; time: string; price: number } | null; lo?: { idx: number; time: string; price: number } | null }

type Ohlcv = { open_time: string; open: number; high: number; low: number; close: number }

type PivotMarker = Awaited<ReturnType<typeof fetchPivotMarkers>>[number]

type FiboOverlay = { hi?: number | null; lo?: number | null; f382?: number | null; f50?: number | null; f618?: number | null }

function parseMs(v: unknown): number | null {
  const ms = Date.parse(String(v || ''))
  return Number.isFinite(ms) ? ms : null
}

function num(v: unknown): number | null {
  const n = Number(v)
  return Number.isFinite(n) ? n : null
}

function asOfMsFromOpportunity(row: any): number | null {
  return (
    parseMs(row?.published_at) ??
    parseMs(row?.created_at) ??
    parseMs(row?.open_time) ??
    null
  )
}

function clampInt(n: number, min: number, max: number): number {
  return Math.max(min, Math.min(max, Math.trunc(n)))
}

function toSec(ms: number): number {
  return Math.floor(ms / 1000)
}

function pivotTypeKeyFromTrend(trend: 'long' | 'short'): 'pivot_high_close' | 'pivot_low_close' {
  return trend === 'long' ? 'pivot_high_close' : 'pivot_low_close'
}

function oppositePivotType(t: 'pivot_high_close' | 'pivot_low_close'): 'pivot_high_close' | 'pivot_low_close' {
  return t === 'pivot_high_close' ? 'pivot_low_close' : 'pivot_high_close'
}

function preferredPivotClass(
  t: 'pivot_high_close' | 'pivot_low_close',
  trend: 'long' | 'short',
): 'HH' | 'HL' | 'LH' | 'LL' | null {
  if (trend === 'long') return (t === 'pivot_high_close' ? 'HH' : 'HL')
  return (t === 'pivot_low_close' ? 'LL' : 'LH')
}

function buildCandleIndexBySec(rows: Ohlcv[]): Map<number, Ohlcv> {
  const map = new Map<number, Ohlcv>()
  for (const r of rows) {
    const ms = parseMs(r.open_time)
    if (ms == null) continue
    map.set(toSec(ms), r)
  }
  return map
}

function pickLastPivot(
  pivots: PivotMarker[],
  type: 'pivot_high_close' | 'pivot_low_close',
  asOfSec: number | null,
  preferredClass?: PivotMarker['classification'],
): PivotMarker | null {
  const eligible = pivots.filter((p) => {
    if (!p || p.type !== type) return false
    if (asOfSec != null && p.time > asOfSec) return false
    return true
  })
  if (!eligible.length) return null
  if (preferredClass) {
    for (let i = eligible.length - 1; i >= 0; i--) {
      if (eligible[i].classification === preferredClass) return eligible[i]
    }
  }
  return eligible[eligible.length - 1]
}

function pickPrevPivotBefore(
  pivots: PivotMarker[],
  type: 'pivot_high_close' | 'pivot_low_close',
  beforeSec: number,
  preferredClass?: PivotMarker['classification'],
): PivotMarker | null {
  const eligible = pivots.filter((p) => p && p.type === type && p.time < beforeSec)
  if (!eligible.length) return null
  if (preferredClass) {
    for (let i = eligible.length - 1; i >= 0; i--) {
      if (eligible[i].classification === preferredClass) return eligible[i]
    }
  }
  return eligible[eligible.length - 1]
}

function deriveAnchorsFromMarketPivots(
  rows: Ohlcv[],
  pivots: PivotMarker[],
  trend: 'long' | 'short',
  asOfSec: number | null,
): { hi: number; lo: number } | null {
  const endType = pivotTypeKeyFromTrend(trend)
  const startType = oppositePivotType(endType)
  const endClass = preferredPivotClass(endType, trend) ?? undefined
  const startClass = preferredPivotClass(startType, trend) ?? undefined

  const endPivot = pickLastPivot(pivots, endType, asOfSec, endClass)
  if (!endPivot) return null
  const startPivot = pickPrevPivotBefore(pivots, startType, endPivot.time, startClass)
    ?? pickPrevPivotBefore(pivots, startType, endPivot.time)
  if (!startPivot) return null

  const bySec = buildCandleIndexBySec(rows)
  const endC = bySec.get(endPivot.time)
  const startC = bySec.get(startPivot.time)
  if (!endC || !startC) return null

  const hi = trend === 'long' ? Number(endC.high) : Number(startC.high)
  const lo = trend === 'long' ? Number(startC.low) : Number(endC.low)
  if (!Number.isFinite(hi) || !Number.isFinite(lo) || hi <= lo) return null
  return { hi, lo }
}

function fractalSwings(rows: Ohlcv[], k = 2): { highs: Array<{ idx: number; time: string; price: number }>; lows: Array<{ idx: number; time: string; price: number }> } {
  const n = Array.isArray(rows) ? rows.length : 0
  const highs: Array<{ idx: number; time: string; price: number }> = []
  const lows: Array<{ idx: number; time: string; price: number }> = []
  if (n === 0) return { highs, lows }
  for (let i = k; i <= n - 1 - k; i++) {
    const curH = Number(rows[i].high)
    const curL = Number(rows[i].low)
    if (!Number.isFinite(curH) || !Number.isFinite(curL)) continue
    let leftH = -Infinity, rightH = -Infinity
    let leftL = Infinity, rightL = Infinity
    for (let j = 1; j <= k; j++) {
      leftH = Math.max(leftH, Number(rows[i - j].high))
      rightH = Math.max(rightH, Number(rows[i + j].high))
      leftL = Math.min(leftL, Number(rows[i - j].low))
      rightL = Math.min(rightL, Number(rows[i + j].low))
    }
    const isHigh = curH > leftH && curH > rightH
    const isLow = curL < leftL && curL < rightL
    if (isHigh) highs.push({ idx: i, time: rows[i].open_time, price: curH })
    if (isLow) lows.push({ idx: i, time: rows[i].open_time, price: curL })
  }
  return { highs, lows }
}

function findCorrectionStartIdx(rows: Ohlcv[], trend: 'long'|'short'): number | null {
  const n = rows.length
  if (n < 2) return null
  for (let i = n - 1; i >= 1; i--) {
    const prev = rows[i - 1]
    const cur = rows[i]
    if (trend === 'long') {
      // Início da correção em alta: fecha abaixo da mínima anterior
      if (Number(cur.close) < Number(prev.low)) return i
    } else {
      // Início da correção em baixa: fecha acima da máxima anterior
      if (Number(cur.close) > Number(prev.high)) return i
    }
  }
  return null
}

// Seleciona o último swing confirmado imediatamente anterior ao início da correção (quando detectado)
function lastSwingByTrend(rows: Ohlcv[], trend: 'long' | 'short', k = 2): Swing | null {
  const { highs, lows } = fractalSwings(rows, k)
  if (!highs.length && !lows.length) return null
  const start = findCorrectionStartIdx(rows, trend)
  const cutoff = (start != null ? Math.max(0, start - 1) : rows.length - 1)

  if (trend === 'long') {
    // Escolher topo como o MAIOR high imediatamente antes do início da correção
    const win = Math.max(10, Math.min(120, Math.floor(rows.length * 0.25)))
    const from = Math.max(0, cutoff - win)
    let hiIdx = -1, hiVal = -Infinity
    for (let i = from; i <= cutoff; i++) { const H = Number(rows[i].high); if (H > hiVal) { hiVal = H; hiIdx = i } }
    // Se nenhum, cai no último pivô confirmado
    let lastHigh = hiIdx >= 0 ? { idx: hiIdx, time: rows[hiIdx].open_time, price: Number(rows[hiIdx].high) } : highs.filter(h => h.idx <= cutoff).slice(-1)[0]
    if (!lastHigh) return null
    // Fundo: último pivô baixo anterior ao topo; se ausente, menor low na janela antes do topo
    let lastLow: { idx: number; time: string; price: number } | null = null
    for (let i = lows.length - 1; i >= 0; i--) { if (lows[i].idx < lastHigh.idx) { lastLow = lows[i]; break } }
    if (!lastLow) {
      const winL = Math.max(20, Math.min(240, Math.floor(rows.length * 0.5)))
      const fromL = Math.max(0, lastHigh.idx - winL)
      let bestIdx = -1, bestL = Infinity
      for (let i = fromL; i <= lastHigh.idx; i++) { const L = Number(rows[i].low); if (L < bestL) { bestL = L; bestIdx = i } }
      if (bestIdx >= 0) lastLow = { idx: bestIdx, time: rows[bestIdx].open_time, price: Number(rows[bestIdx].low) }
    }
    if (!lastLow) return null
    return { hi: lastHigh, lo: lastLow }
  } else {
    // Baixa: escolher fundo como o MENOR low antes do início da correção
    const win = Math.max(10, Math.min(120, Math.floor(rows.length * 0.25)))
    const from = Math.max(0, cutoff - win)
    let loIdx = -1, loVal = Infinity
    for (let i = from; i <= cutoff; i++) { const L = Number(rows[i].low); if (L < loVal) { loVal = L; loIdx = i } }
    let lastLow = loIdx >= 0 ? { idx: loIdx, time: rows[loIdx].open_time, price: Number(rows[loIdx].low) } : lows.filter(l => l.idx <= cutoff).slice(-1)[0]
    if (!lastLow) return null
    // Topo: último pivô alto anterior; se ausente, maior high na janela antes do fundo
    let lastHigh: { idx: number; time: string; price: number } | null = null
    for (let i = highs.length - 1; i >= 0; i--) { if (highs[i].idx < lastLow.idx) { lastHigh = highs[i]; break } }
    if (!lastHigh) {
      const winH = Math.max(20, Math.min(240, Math.floor(rows.length * 0.5)))
      const fromH = Math.max(0, lastLow.idx - winH)
      let bestIdx = -1, bestH = -Infinity
      for (let i = fromH; i <= lastLow.idx; i++) { const H = Number(rows[i].high); if (H > bestH) { bestH = H; bestIdx = i } }
      if (bestIdx >= 0) lastHigh = { idx: bestIdx, time: rows[bestIdx].open_time, price: Number(rows[bestIdx].high) }
    }
    if (!lastHigh) return null
    return { hi: lastHigh, lo: lastLow }
  }
}

export function computeFiboFromSwing(hi: number, lo: number): { f382: number; f50: number; f618: number } {
  const H = Number(hi), L = Number(lo)
  const R = H - L
  return { f382: H - 0.382 * R, f50: H - 0.5 * R, f618: H - 0.618 * R }
}

function deriveFiboFromConfluence(row: any): FiboOverlay | null {
  const cf = (row?.confluence_factors ?? row?.confluence ?? {}) as any
  const anchor = cf?.fibo_anchor as any
  if (!anchor || typeof anchor !== 'object') return null
  const p0 = num(anchor?.point0?.price)
  const p1 = num(anchor?.point1?.price)
  if (p0 == null || p1 == null) return null

  const hi = Math.max(p0, p1)
  const lo = Math.min(p0, p1)
  if (!Number.isFinite(hi) || !Number.isFinite(lo) || hi <= lo) return null

  const { f382, f50, f618 } = computeFiboFromSwing(hi, lo)
  return { hi, lo, f382, f50, f618 }
}

async function deriveFiboFromRpc(params: {
  symbol: string
  tf: string
  trend: string
  asOfMs: number | null
}): Promise<FiboOverlay | null> {
  try {
    const asOfIso = params.asOfMs != null ? new Date(params.asOfMs).toISOString() : new Date().toISOString()
    const { data, error } = await supabase.rpc('compute_opportunity_fibo_payload', {
      p_symbol: params.symbol,
      p_target_timeframe: params.tf,
      p_target_trend: params.trend,
      p_as_of: asOfIso,
    })
    if (error) return null
    if (!data) return null
    return deriveFiboFromConfluence({ confluence_factors: data })
  } catch {
    return null
  }
}

export async function deriveFiboForOpportunity(row: any, opts?: { limit?: number; k?: number }): Promise<FiboOverlay | null> {
  try {
    const symbol = String(row?.symbol || '').toUpperCase()
    let tf = String(row?.target_timeframe || row?.timeframe || '').toUpperCase()
    if (!symbol || !tf) return null
    if (tf === '1D') tf = 'D'; if (tf === '1W') tf = 'W'; if (tf === '1M') tf = 'M'
    const limit = clampInt(Number(opts?.limit || 300), 50, 1000)
    const k = clampInt(Number(opts?.k || 2), 2, 5)

    // SSOT: sempre tenta calcular no Supabase com as regras oficiais (market_pivots + candle wick).
    // Mantem o front alinhado com ajustes de bucket/resolucao (D/W/M), sem depender de cache/stored antigo.
    const asOfMs = asOfMsFromOpportunity(row)
    const trendRaw = String(row?.target_trend || (row?.confluence_factors || {}).trend_alvo || '').toLowerCase().trim()
    const trendForRpc = trendRaw || (
      row?.opportunity_type === 'OVERSOLD_BULLISH' ? 'bullish' :
        row?.opportunity_type === 'OVERBOUGHT_BEARISH' ? 'bearish' :
          ''
    )
    if (trendForRpc) {
      const fromRpc = await deriveFiboFromRpc({ symbol, tf, trend: trendForRpc, asOfMs })
      if (fromRpc) return fromRpc
    }

    // Fallback: se a oportunidade ja possui Fibo persistido em confluence_factors (apos publish), usa diretamente.
    const fromCf = deriveFiboFromConfluence(row)
    if (fromCf) return fromCf

    const candles = await fetchCandles({ symbol, timeframe: tf as any, limit })
    const rawRows = Array.isArray(candles) ? (candles as Ohlcv[]) : []
    const rows = asOfMs != null
      ? rawRows.filter((r) => {
        const ms = parseMs(r.open_time)
        return ms != null && ms <= asOfMs
      })
      : rawRows
    if (!rows.length) return null
    const tr = String(row?.target_trend || (row?.confluence_factors||{}).trend_alvo || '').toLowerCase()
    const trend: 'long'|'short' = (tr === 'bearish' || tr === 'baixa' || tr === 'short') ? 'short' : 'long'

    let anchors: { hi: number; lo: number } | null = null
    try {
      const pivots = await fetchPivotMarkers(symbol, tf as any, 600)
      anchors = deriveAnchorsFromMarketPivots(rows, pivots, trend, asOfMs != null ? toSec(asOfMs) : null)
    } catch {
      anchors = null
    }

    if (!anchors) {
      const sw = lastSwingByTrend(rows, trend, k)
      if (sw?.hi?.price != null && sw?.lo?.price != null) {
        anchors = { hi: Number(sw.hi.price), lo: Number(sw.lo.price) }
      }
    }

    if (!anchors) return null
    const H = Number(anchors.hi)
    const L = Number(anchors.lo)
    if (!Number.isFinite(H) || !Number.isFinite(L) || H <= L) return null
    const { f382, f50, f618 } = computeFiboFromSwing(H, L)
    return { hi: H, lo: L, f382, f50, f618 }
  } catch {
    return null
  }
}
